import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Card, IconCard, List, TextCard } from '@pella/sash-framework';
import { classNames } from '@pella/sash-framework/dist/utilities';
import { CTA, Image } from '../../../';
import { isValidArray } from '../../../utilities/validations';

import BaseImageCard from '../../image-card';
import TextOnlyCard from '../text-only-card';

const CardRenderer = ({ data, pdpUrl, showCtas = true, children, ...other }) => {
	if (!data?.card_type && !data?.style) return null;

	// TODO: Remove once transition to data.card is complete in Contentstack
	const cardType = data.style || data.card_type;
	const cardSeoTag = data.seo_heading || data.seo_tag || data.heading?.replace(/(<([^>]+)>)/gi, '');
	const cardMedia = data.media || data.image;
	const cardIcon = data.material_symbol || data.material_icon;
	const cardBody = data.description;
	const cardCtas = data.cta || data.ctas;

	// CTA List Classes for 'standard' card type
	const standardListClasses = classNames({
		'space-y-2': true,
		'pt-4': !data.variant || data.variant === 'standard-padded', // remove data.variant === 'standard-padded' once standard-padded has been successfully removed
		'px-6 pb-6': data.variant === 'standard-padded-content',
		'pt-2': data.variant === 'award',
		'mt-auto': ((!data.variant || data.variant === 'standard-padded') && isValidArray(cardMedia)) || data.variant === 'standard-full' || data.variant === 'standard-padded-content',
	});

	const renderImageBadges = (badges, truncate = false) => (
		<List
			bullets='none'
			layout='inline'
			className={`z-2 absolute !m-0 space-x-1 ${truncate ? 'left-1 top-1' : 'left-2 top-2'}`}
		>
			{badges.map((badge, index) => {
				if (!badge.color || !badge.badge) return null;

				return (
					<li key={index}>
						<Badge
							color={badge.color}
							dangerouslySetInnerHTML={{ __html: badge.badge.substring(0, truncate ? 20 : 100) }}
							className='!border-0 !px-1 !pb-1'
							size='md'
						/>
					</li>
				);
			})}
		</List>
	);

	switch (cardType) {
		case 'icon':
			return (
				<IconCard
					variant={data.variant}
					{...other}
				>
					{cardCtas?.length === 1 && (
						<CTA
							data={cardCtas[0]}
							className='cardLink -indent-full absolute left-0 top-0 h-full w-full'
							renderAs='link'
						>
							{cardSeoTag}
						</CTA>
					)}

					{cardIcon && <IconCard.Icon name={cardIcon} />}
					{isValidArray(cardMedia) && (
						<>
							{isValidArray(cardMedia[0].badge) && renderImageBadges(cardMedia[0].badge)}

							<IconCard.Image
								src={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.src || cardMedia[0].image?.url}
								srcSet={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.srcSet}
								sizes={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.sizes}
							/>
						</>
					)}

					{data.file?.url && (
						<IconCard.Image
							src={data.file.gatsbyImageData?.images?.fallback?.src || data.file.url}
							srcSet={data.file.gatsbyImageData?.images?.fallback?.srcSet}
							sizes={data.file.gatsbyImageData?.images?.fallback?.sizes}
						/>
					)}

					{data.heading && <IconCard.Heading content={data.heading} />}
					{data.subheading && <div dangerouslySetInnerHTML={{ __html: data.subheading }} />}

					<IconCard.Content content={cardBody}>
						{showCtas && isValidArray(cardCtas) && (
							<List
								bullets='none'
								className='mt-auto pt-5'
							>
								{cardCtas.map((cta, index) => (
									<li key={index}>
										<CTA
											data={cta}
											size={!cta.button_style?.includes('tertiary') ? 'sm' : null}
											wide={cta.gtm_link_type === 'button' ? true : null}
											className={`relative ${cta.button_style?.includes('tertiary') ? 'decoration-black' : ''}`}
										/>
									</li>
								))}
							</List>
						)}
					</IconCard.Content>
				</IconCard>
			);

		case 'standard':
			// remove variant mapping once standard-padded has been successfully removed
			const isBeforeAfter = cardMedia?.length > 1 ? true : false;

			return (
				<Card
					variant={!data.variant || data.variant === 'standard-padded' ? null : data.variant}
					horizontal={data.horizontal}
					{...other}
				>
					{cardCtas?.length === 1 && (
						<CTA
							data={cardCtas[0]}
							className='cardLink z-2 -indent-full absolute left-0 top-0 h-full w-full'
							renderAs='link'
						>
							{cardSeoTag}
						</CTA>
					)}

					{isBeforeAfter && isValidArray(cardMedia) && (
						<div className='relative mb-4 flex aspect-[4/3] overflow-hidden'>
							{cardMedia?.map((image, index) => (
								<div
									key={`image-${index}`}
									className='relative flex-1'
								>
									<Badge
										color='black'
										className='z-2 absolute left-2 top-2 xl:left-2.5 xl:top-2.5'
									>
										{index === 0 ? 'Before' : 'After'}
									</Badge>

									<Image
										data={image}
										params='?format=pjpg&auto=webp&quality=90'
										className='h-full object-cover'
									/>
								</div>
							))}
						</div>
					)}

					{!isBeforeAfter && isValidArray(cardMedia) && cardMedia[0]?.image && (
						<Card.Image
							src={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.src || cardMedia[0].image?.url + '?format=pjpg&auto=webp&quality=90'}
							srcSet={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.srcSet}
							sizes={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.sizes}
							objectFit={cardMedia[0].contain ? 'contain' : 'cover'}
						/>
					)}

					{!isBeforeAfter && !isValidArray(cardMedia) && cardMedia?.url && <Card.Image src={cardMedia.url + '?format=pjpg&auto=webp&quality=90'} />}

					{isValidArray(cardMedia) && cardMedia[0]?.video && (
						<Card.Video
							imageSrc={cardMedia[0]?.image_overlay?.[0]?.image?.url + '?format=pjpg&auto=webp&quality=90'}
							videoData={{
								id: cardMedia[0].video.video_id,
								title: cardMedia[0].video.title,
								transcript: cardMedia[0].transcript,
							}}
						/>
					)}

					{data.heading && (
						<Card.Heading
							content={data.heading}
							className={!cardBody ? 'text-center text-lg' : null}
						/>
					)}

					{data.subheading && <div dangerouslySetInnerHTML={{ __html: data.subheading }} />}

					{!data.horizontal ? (
						<>
							{cardBody && <Card.Content content={cardBody} />}
							{showCtas && isValidArray(cardCtas) && (
								<List
									bullets='none'
									className={standardListClasses}
								>
									{cardCtas.map((cta, index) => (
										<li key={index}>
											<CTA
												data={cta}
												size={!cta.button_style?.includes('tertiary') ? 'sm' : null}
												wide={true}
												className={`relative ${cta.button_style?.includes('tertiary') ? `${cta.material_icon ? 'flex-row-reverse [&>.material-symbols-sharp]:!m-0' : ''} ${cta.material_icon && cardBody ? '!justify-end' : !cta.material_icon && cardBody ? '!justify-start' : ''}` : ''}`}
											/>
										</li>
									))}
								</List>
							)}
						</>
					) : (
						<Card.Content content={cardBody}>
							{showCtas && isValidArray(cardCtas) && (
								<List
									bullets='none'
									className='mt-5 md:mt-6'
								>
									{cardCtas.map((cta, index) => (
										<li key={index}>
											<CTA
												data={cta}
												className='!justify-start !text-left'
											/>
										</li>
									))}
								</List>
							)}
						</Card.Content>
					)}

					{children}
				</Card>
			);

		case 'box-card':
		case 'text-card':
			// remove data variant mapping once they have been successfully removed in CS data
			return (
				<TextCard
					variant={!data.variant || data.variant === 'inline-heading-icon' || data.variant === 'centered-no-icon' ? null : data.variant}
					{...other}
				>
					{cardCtas?.length === 1 && (
						<CTA
							data={cardCtas[0]}
							className='cardLink -indent-full absolute left-0 top-0 h-full w-full'
							renderAs='link'
						>
							{cardSeoTag}
						</CTA>
					)}

					{data.heading && (
						<TextCard.Heading content={data.heading}>
							{cardIcon && <TextCard.Icon name={cardIcon} />}
							{isValidArray(cardMedia) && (
								<TextCard.Image
									src={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.src || cardMedia[0].image?.url}
									srcSet={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.srcSet}
									sizes={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.sizes}
								/>
							)}
							{data.svg && <div dangerouslySetInnerHTML={{ __html: data.svg }} />}
						</TextCard.Heading>
					)}

					<TextCard.Content content={cardBody}>
						{data.subheading && <div dangerouslySetInnerHTML={{ __html: data.subheading }} />}
						{showCtas && isValidArray(cardCtas) && (
							<List bullets='none'>
								{cardCtas.map((cta, index) => (
									<li key={index}>
										<CTA
											data={cta}
											className='relative'
											size={cta.button_style !== 'tertiary' ? 'sm' : null}
											wide={cta.gtm_link_type === 'button' ? true : null}
										/>
									</li>
								))}
							</List>
						)}
					</TextCard.Content>
				</TextCard>
			);

		case 'image-card':
			return (
				<BaseImageCard
					data={{
						heading: data.heading,
						image: cardMedia,
						ctas: cardCtas,
					}}
					{...other}
				/>
			);

		case 'cob':
			return (
				<Card
					className='cobCard border-2 border-transparent text-center hover:border-black'
					{...other}
				>
					{isValidArray(cardCtas) ? (
						<CTA
							data={cardCtas[0]}
							renderAs='link'
							className='cardLink block h-full p-4 md:p-6'
						>
							{isValidArray(cardMedia) && (
								<Card.Image
									src={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.src || cardMedia[0].image?.url}
									srcSet={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.srcSet}
									sizes={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.sizes}
									objectFit='contain'
								/>
							)}

							{data.heading && (
								<Card.Heading
									className='text-base !font-normal md:!text-lg'
									content={data.heading}
								/>
							)}
						</CTA>
					) : (
						<>
							{isValidArray(cardMedia) && (
								<Card.Image
									src={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.src}
									srcSet={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.srcSet}
									sizes={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.sizes}
								/>
							)}

							{data.heading && (
								<Card.Heading
									className='text-base !font-normal md:!text-lg'
									content={data.heading}
								/>
							)}
						</>
					)}
				</Card>
			);

		case 'product':
			return (
				<Card
					className={`productCard ${!data.left_align ? 'text-center' : ''}`}
					{...other}
				>
					{isValidArray(cardMedia) && (
						<Card.Image
							src={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.src || cardMedia[0].image?.url}
							srcSet={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.srcSet}
							sizes={cardMedia[0].image?.gatsbyImageData?.images?.fallback?.sizes}
							objectFit='contain'
						/>
					)}

					{data.heading && (
						<Card.Heading
							content={data.heading}
							className='text-xl'
						/>
					)}

					<Card.Content
						className='mb-4 text-base md:text-lg [&_li]:py-2.5 [&_ul]:m-0 [&_ul]:list-none [&_ul]:divide-y [&_ul]:divide-neutral-200 [&_ul]:p-0'
						content={cardBody}
					/>

					{isValidArray(cardCtas) &&
						(pdpUrl === cardCtas[0]?.link?.href ? (
							<p className='mt-auto text-sm font-semibold'>( Currently Viewing )</p>
						) : (
							<CTA
								data={cardCtas[0]}
								size='sm'
								wide={cardCtas[0].gtm_link_type === 'button' ? true : null}
								className='mt-auto self-center'
							/>
						))}
				</Card>
			);

		case 'yellow':
			return (
				<TextOnlyCard
					data={data}
					{...other}
				/>
			);

		default:
			return null;
	}
};

CardRenderer.propTypes = {
	data: PropTypes.object,
	pdpUrl: PropTypes.string,
	showCtas: PropTypes.bool,
	children: PropTypes.node,
};

export default CardRenderer;
